import type { SignalBar } from '../../utils/signalBars'
import { SIGNAL_QUALITY_LABELS, type SignalDay, type SignalQuality } from '../../types/signalStrength'
import { BarDateLabel } from './DeliveryBars'

/**
 * The daily signal bars, one per UTC day, oldest on the left. Shared by the
 * device card's Daily signal strip and the dashboard's best/worst signal
 * lists, so the two always read the same way.
 *
 * One blue, light to dark for weak to strong. Red, green and grey belong to
 * the missed-messages strip; reusing them here would make a red signal day
 * over a red delivery day read as cause and effect, which is the one thing
 * these two charts must not say.
 *
 * Colour is the ONLY channel: bars are uniform height, because scaling them
 * by margin collapsed a critical device to a row of slivers. A day with no
 * data stays a stub, so "heard nothing" is structural rather than a second
 * shade to tell apart.
 */

export const SIGNAL_BAR_TONES: Record<SignalQuality, string> = {
  critical: 'bg-sky-200',
  weak: 'bg-sky-300',
  fair: 'bg-sky-500',
  good: 'bg-sky-700',
  unknown: 'bg-gray-100',
}

const SIGNAL_LEGEND: { quality: SignalQuality; label: string }[] = [
  { quality: 'critical', label: 'Critical <4 dB' },
  { quality: 'weak', label: 'Weak 4–10' },
  { quality: 'fair', label: 'Fair' },
  { quality: 'good', label: 'Good 16+' },
  { quality: 'unknown', label: 'No data' },
]

export default function SignalBars({
  bars,
  heightClass = 'h-10',
  maxBarWidth = 26,
  stretch = false,
  showDates = true,
}: {
  bars: SignalBar[]
  heightClass?: string
  /** Caps the strip's width so a short window does not stretch a few bars. */
  maxBarWidth?: number
  /** Fill the available width regardless of bar count, for rows of strips that must line up. */
  stretch?: boolean
  showDates?: boolean
}) {
  if (bars.length === 0) return null

  return (
    <div style={stretch ? undefined : { maxWidth: bars.length * maxBarWidth }}>
      <div className={`flex items-end gap-px ${heightClass}`} role="img" aria-label={signalStripLabel(bars)}>
        {bars.map((bar) => (
          <span key={bar.day} title={signalBarTitle(bar)} className="group relative flex h-full min-w-0 flex-1 items-end">
            <BarDateLabel day={bar.day} />
            <span
              className={`w-full rounded-sm ${SIGNAL_BAR_TONES[bar.quality]}`}
              style={{ height: bar.row ? '100%' : '12%' }}
            />
          </span>
        ))}
      </div>
      {showDates && (
        <div className="mt-1 flex justify-between gap-2 text-[11px] tabular-nums text-gray-500">
          <span>{bars[0].day}</span>
          {bars.length > 1 && <span>{bars[bars.length - 1].day}</span>}
        </div>
      )}
    </div>
  )
}

/** The ratings in words, so the bars never rely on colour alone. */
export function SignalLegend({ className = '' }: { className?: string }) {
  return (
    <ul className={`flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-600 ${className}`}>
      {SIGNAL_LEGEND.map(({ quality, label }) => (
        <li key={quality} className="flex items-center gap-1">
          <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-sm ${SIGNAL_BAR_TONES[quality]}`} />
          {label}
        </li>
      ))}
    </ul>
  )
}

export function signalBarTitle(bar: SignalBar): string {
  const row: SignalDay | null = bar.row
  if (!row) return `${bar.day}: no signal recorded`
  const range = row.rssiMin !== null && row.rssiMax !== null ? `, range ${row.rssiMin} to ${row.rssiMax}` : ''
  const margin = row.marginDb === null ? '' : ` (${Math.round(row.marginDb)} dB above floor)`
  return `${bar.day}: ${SIGNAL_QUALITY_LABELS[row.quality]}, median ${row.rssiMedian ?? '—'} dBm${margin}${range}, ${
    row.sampleCount
  } messages, ${row.stationCount} station${row.stationCount === 1 ? '' : 's'}${stationsSuffix(row.stations)}`
}

/** "12E25 64, 8C09 27" - who carried the day, for tooltips and the missed-days table. */
export function stationsText(stations: SignalDay['stations']): string {
  return (stations ?? []).map((s) => `${s.id} ${s.count}`).join(', ')
}

function stationsSuffix(stations: SignalDay['stations']): string {
  const text = stationsText(stations)
  return text ? ` (${text})` : ''
}

/** One sentence for screen readers, since the bars themselves are hover-only. */
export function signalStripLabel(bars: SignalBar[]): string {
  const withData = bars.filter((b) => b.row)
  const counts = withData.reduce<Record<string, number>>((acc, b) => {
    acc[b.quality] = (acc[b.quality] ?? 0) + 1
    return acc
  }, {})
  const parts = (['critical', 'weak', 'fair', 'good'] as const)
    .filter((q) => counts[q])
    .map((q) => `${counts[q]} ${SIGNAL_QUALITY_LABELS[q].toLowerCase()}`)
  return `Daily signal over ${bars.length} days: ${withData.length} days with data${parts.length ? ` (${parts.join(', ')})` : ''}.`
}
